import { Modal, Spin, notification } from "antd";
import _ from 'lodash-es';
import {forwardRef, useImperativeHandle, useRef, useState, useEffect} from 'react';

import Evisit from "../../libs/Evisit";
import request from "../../libs/request";
import Reservation from "./components/Reservation";
import FormComponent from "./components/FormComponent";
import LoginComponent from './components/LoginComponent';
import GuestIntake from './components/GuestIntake';
import Intake from './components/Intake';
import CalendarPicker from './components/CalendarPicker';
import Consultation from './components/Consultation';
import SetupSession from './components/SetupSession';
import TestDevice from './components/TestDevice';
import Confirmation from './components/Confirmation';
import DependantList from './components/DependantList';
import Emr from './components/Emr';
import PaymentOptions from './components/PaymentOptions';
import Insurance from "./components/Insurance";
import SearchPatient from "./components/SearchPatient";
import WalkinConfirmation from "./components/WalkinConfirmation";
import EconsultConfirmmation from "./components/EconsultConfirmmation";
import PhoneCallbackConfirmation from "./components/PhoneCallbackConfirmation";

var componentStates = {
    payment: {
        value: {
            amountOff: 0,
            totalAmount: 0
        }
    }
};

const COMPONENT_END_ID = 'end';

const isLoggedIn = () => {
    const userToken = _.get(window, "user.token"); 
    if (!userToken || userToken.length === 0) {
        return false;
    }
    if (userToken === _.get(window, 'CONFIG.Account.waiting_room.visits.appointment_v2.guest_user')) {
        // The session is using tmp token.
        return false;
    }
    
    return true;
}

const workflowScreenInfo = ({currentComponent, stack, workflow}) => {
    return {
        "componentId": _.get(currentComponent, "id"),
        "hasPrevious": stack.length > 2,
        "currentIndex": _.findIndex(workflow["components"], {component_id: currentComponent["id"]}),
        "workflowId": _.get(workflow, "id"),
    };
}

const Workflow = forwardRef((props, ref) => {
    const [workflow, setWorkflow] = useState(null);
    const [stack, setStack] = useState([]);
    const [currentComponentId, setCurrentComponentId] = useState(null);
    const [workflowComponentId, setWorkflowComponentId] = useState(null);
    const [openModal, setOpenModal] = useState(false);
    const [components, setComponents] = useState([]);

    let roomData = _.get(componentStates, "room.value");
    let intakeData = _.get(componentStates, "intake.value");
    let paymentData = _.get(componentStates, "payment.value");
    let visitData = _.get(componentStates, "visit.value");
    const reservationRef = useRef();
    const queryParams = new URLSearchParams(_.get(window, "location.search"));
    const isMobileWebview = queryParams.get("mobile") === "true";
    var currentComponent = _.find(components, {id: currentComponentId})

    useEffect(() => {
        if (props.room) {
            _.set(componentStates, "room.value", props.room);
        }
        if (props.providers) {
            _.set(componentStates, "providers.value", props.providers);
        }
        if (props.visit) {
            _.set(componentStates, "visit.value", props.visit);
        }

        if (props.patient) {
            _.set(componentStates, "patient.value", props.patient);
        }
    }, [props]);

    useEffect(() => {
        if (workflow && components && components.length > 0) {
            proceedWorkflow(workflow["components"], null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workflow, components]);

    useEffect(() => {
        if (currentComponent) {
            const payload = workflowScreenInfo({currentComponent, stack, workflow});
            window.workflowScreenInfo = payload;
            Evisit.postMessageBackToParent({
                name: "workflow.screenLoaded",
                data: payload
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentComponent]);

    const endWorkflow = () => {
        setOpenModal(false);
        setCurrentComponentId(null);
        const result = {};
        
        if (isLoggedIn()) {
            result["user"] = window.user;
        }
        if (!_.get(workflow, "login", true)) {
            result["login"] = false;
        }
        if (_.get(workflow, "options.on_completed.next.stay_in_page")) {
            result["stay_in_page"] = true;
        } else {
            if (_.get(componentStates, "visit.value.type") === window.CONST.VISIT.TYPE.WALKIN) {
                result["next"] = "/visits/start/" + componentStates.visit.value.id;
                if (currentComponent.type === 'walkin_confirmation'
                    && window.CONFIG?.Account?.telemedicine_resource_workflow?.enabled
                ) {
                    result['next'] = '/visits/join_call/' + componentStates.visit.value.code;
                }
            }
            if (_.has(currentComponent, "next.url")) {
                result["next"] = _.get(currentComponent, "next.url");
            }
        }
        if (_.get(componentStates, "visit.value.id")) {
            result["visit"] = _.get(componentStates, "visit.value");
        }
        if (_.get(componentStates, "intake.value.id")) {
            result["intake"] = _.get(componentStates, "intake.value");
        }

        if (props["onEnd"]) {
            props["onEnd"]();
        }

        Evisit.postMessageBackToParent({
            name: "workflow.completed",
            data: result
        });
    };

    const onCancelFlow = () => {
        setOpenModal(false);
        if (workflow["on_cancel"] && workflow["on_cancel"].length > 0) {
            setWorkflowComponentId(currentComponentId);
            setCurrentComponentId(workflow["on_cancel"][0]["component_id"]);
            setTimeout(function() {
                // Somehow if we open modal immediately after closing it, the animation effect will be gone
                setOpenModal(true);
            }, 100);
            return;
        }
        var result = {};
        if (_.get(workflow, "options.on_completed.next.stay_in_page")) {
            result["stay_in_page"] = true;
        }
        if (props["onEnd"]) {
            props["onEnd"]();
        }
        Evisit.postMessageBackToParent({
            name: "workflow.completed",
            data: result
        });
    }

    const inCancelFlow = () => {
        return workflow["on_cancel"] && _.findIndex(workflow["on_cancel"], {component_id: currentComponentId}) > -1;
    }

    const getPreviousIndex = () => {
        var index = -1;
        var previousIndex = -1;
        var updatedStack = Array.from(stack);
        if (inCancelFlow()) {
            previousIndex = _.findIndex(workflow["components"], {component_id: workflowComponentId});
        } else if (updatedStack.length > 0) {
            updatedStack.pop();
            previousIndex = _.findIndex(workflow["components"], {component_id: _.last(updatedStack)});
            do {
                index = previousIndex;
                if (shouldSkipComponent(previousIndex)) {
                    updatedStack.pop();
                    var previousId = _.last(updatedStack);
                    if (previousId) {
                        previousIndex = _.findIndex(workflow["components"], {component_id: previousId});
                    } else {
                        previousIndex = -1;
                        break;
                    }
                }
            } while (index != previousIndex);
            setStack(updatedStack);
        }

        return previousIndex;
    }

    const onPrevious = ({formData}) => {
        if (formData) {
            componentStates[currentComponent["key"]]["value"] = formData;
        }
        const previousIndex = getPreviousIndex();

        if (previousIndex >= 0) {
            setOpenModal(false);
            setCurrentComponentId(workflow["components"][previousIndex]["component_id"]);

            setTimeout(function() {
                // Somehow if we open modal immediately after closing it, the animation effect will be gone
                setOpenModal(true);
            }, 100);
        }
    }

    const componentIsReady = (type) => {
        /*
        var state = componentStates[type];
        for (var i in state.dependencies) {
            if (componentStates[state.dependencies[i]]["status"] !== "ready") {
                return false;
            }
        }
        */
        return true;
    }

    const shouldSkipComponent = (index) => {
        let component = _.find(components, {id: workflow["components"][index]["component_id"]});
        if (component["type"] === "payment_options" && paymentData.totalAmount === 0) {
            return true;
        }
        var show = _.get(component, "conditional.show", true);

        if (component["conditional"] && component["conditional"]["when"]) {
             // The component code can access 'componentStates' via 'data'
            // eslint-disable-next-line no-unused-vars
            var data = componentStates;
            // eslint-disable-next-line no-eval
            eval(component["conditional"]["when"]);
            if (!show) {
                return true;
            }
        }
        return false;
    }

    const processData = async (result) => {
        // 1. Send request for module if the dependencies are met
        let componentType = currentComponent["type"];
        if (componentType === "calendar_picker" && componentIsReady(componentType)) {
            _.set(componentStates, "calendar_picker.value", result);
        }
        if (["consultation"].indexOf(componentType) > -1) {
            var intake_id = _.get(intakeData, "id");
            intake_id = intake_id ? "/" + intake_id : "";
            const response = await request.post(`${window.base_url}/api_v3/intakes${intake_id}`, result);
            _.set(componentStates, "intake.value", response["data"])
            if (_.has(response, "data.consultation.amount")) {
                paymentData = {
                    ...paymentData,
                    totalAmount: _.get(response, "data.consultation.amount", 0),
                };
                _.set(componentStates, "payment.value", paymentData);
            }
        } else if (componentType === "login") {
            await updateRelatedComponentAfterLogin();
        }
        // sometimes component key not exist in componentStates, should init it first
        _.assign(componentStates, { [currentComponent["key"]]: componentStates[currentComponent["key"]] || {} });
        componentStates[currentComponent["key"]]["status"] = "ready";
        componentStates[currentComponent["key"]]["value"] = result;
        if (!componentStates[currentComponent["code"]]) {
            componentStates[currentComponent["code"]] = {
                "value": result
            };
        } else {
            componentStates[currentComponent["code"]] = {
                "value": _.defaultsDeep(result, componentStates[currentComponent["code"]]["value"])
            };
        }
    }

    const updateRelatedComponentAfterLogin = async () => {
        var intakeData = _.get(componentStates, "intake.value");
        if (intakeData && intakeData.id && window.user.id) {
            const response = await request.post(`${window.base_url}/api_v3/intakes/${intakeData.id}`,  {
                member_id: window.user.id
            });
            _.set(componentStates, "intake.value", response["data"]);
        }
    }

    const handleOnNext = async (result) => {
        await processData(result);
        if (inCancelFlow()) {
            return proceedWorkflow(workflow["on_cancel"], currentComponentId);
        }

        return proceedWorkflow(workflow["components"], currentComponentId);
    }

    const proceedWorkflow = (list, currentId) => {
        var index = -1, nextIndex = 0;
        const nextComponentId = _.get(currentComponent, "next_component_id");
        if (currentComponent && currentComponent["next_component_id"]) {
            nextIndex = _.findIndex(list, {component_id: nextComponentId});
        } else if (currentId !== null) {
            nextIndex = _.findIndex(list, {component_id: currentId}) + 1;
        }
        while ((nextIndex !== index) && (nextIndex <= list.length -1)) {
            index = nextIndex;
            if (shouldSkipComponent(nextIndex)) {
                // If the next component will be skipped, check the next
                nextIndex ++;
            }
        }
        if ((nextIndex > list.length - 1) || nextComponentId === COMPONENT_END_ID) {
            if (props.endWorkflow) {
                props.endWorkflow();
            } else {
                endWorkflow();
            }
            return;
        }

        setOpenModal(false);
        setTimeout(function() {
            setCurrentComponentId(list[nextIndex]["component_id"]);
            var updatedStack = Array.from(stack);
            updatedStack.push(list[nextIndex]["component_id"]);
            setStack(updatedStack);
            // Somehow if we open modal immediately after closing it, the animation effect will be gone
            setOpenModal(true);
        }, 100);
    };

    const modalWorkflow = () => {
        let component =
            <div
                className="is-spinning-loading"
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "50vh",
                }}
            >
                <Spin size="large" />
            </div>;

        const componentType = _.get(currentComponent, "type");
        var props = {
            onNext: handleOnNext,
            endWorkflow: endWorkflow,
            onPrevious: (stack.length > 1 || inCancelFlow()) ? onPrevious : null,
            room: roomData,
            visit: visitData,
            currentComponent: currentComponent,
            data: componentStates,
            isMobileWebview: isMobileWebview,
            workflow: workflow,
        }
        switch (componentType) {
            case "login":
                component = <LoginComponent 
                    { ...props }
                />;
                break;   
            case "intake":
                component = <Intake 
                    { ...props }
                />;
                break;   
            case "consultation":
                component = <Consultation
                    { ...props }
                />;
                break;
            case "payment_options":
                component = (
                    <PaymentOptions
                        { ...props }
                    />
                );
                break;
            case "calendar_picker":
                component = <CalendarPicker {...props} reservationRef={reservationRef}/>;
                break;
            case "test_device":
                component = <TestDevice 
                    { ...props }
                />;
                break;
            case "form":
                component = <FormComponent
                    { ...props }
                />;
                break;
            case "setup_session":
                component = <SetupSession
                    { ...props }
                />;
                break;
            case "confirmation":
                component = <Confirmation
                    { ...props }
                    reservationRef={reservationRef}
                />;
                break;
            case "dependant_list":
                component = <DependantList
                    { ...props }
                />;
                break;
            case "emr":
                component = <Emr
                    { ...props }
                />;
                break;
            case "insurance":
                component = <Insurance { ...props }/>;
                break;
            case "search_patient":
                component = <SearchPatient { ...props }/>;
                break;
            case "walkin_confirmation":
                component = <WalkinConfirmation { ...props }/>;
                break;
            case "econsult_confirmation":
                component = <EconsultConfirmmation { ...props }/>;
                break;
            case "phone_callback_confirmation":
                component = <PhoneCallbackConfirmation { ...props }/>;
                break;
            default:
                break;
        }
        var modalProps = {
            title: _.get(currentComponent, "title", _.get(workflow, "data.title", "")),
            open: openModal,
            onCancel: onCancelFlow,
            footer: null,
            maskClosable: false,
            className: "workflow-modal",
            width: isMobileWebview ? "100%" : "600px",
            closeIcon: !isMobileWebview
        };
        if (currentComponent && currentComponent["options"]) {
            modalProps = _.assign(modalProps, _.pick(currentComponent["options"], ['width', 'closeIcon']));
        }

        return (
            <Modal {...modalProps} className={isMobileWebview ? "modal-mobile-webview" : ""}>
                { _.get(window.CONFIG, "Account.mps_booking.reservations.enabled", false) && 
                    <Reservation ref={reservationRef} />
                }
                { component }
            </Modal>
        );
    };

    const inlineWorkflow = () => {
        let component =
            <div
                className="is-spinning-loading"
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "50vh",
                }}
            >
                <Spin size="large" />
            </div>;
        const componentType = _.get(currentComponent, "type");
        switch (componentType) {
            case "guest_intake":
                if (!roomData) {
                    break;
                }
                component = <GuestIntake
                    onNext={handleOnNext}
                    room={roomData}
                    currentComponent={currentComponent}
                />;
                break;
            case "intake":
                component = <Intake onNext={handleOnNext}/>;
                break;
            case "setup_session":
                component = <SetupSession onNext={handleOnNext}/>;
                break;
            default:
                break;
        }
        return (
            <div className="inline-workflow">
                { component }
            </div>
        );
    };

    const start = (workflow, components) => {
        if (!workflow["components"] || workflow["components"].length === 0) {
            notification['error']({
                message: 'Error',
                placement: 'top',
                description: "The workflow contains no components",
            });
            return;
        }
        // componentStates = initComponentStates;
        setWorkflow(workflow);
        setComponents(components);
        var componentKeys = _.map(components, "key");
        for (var i in componentKeys) {
            componentStates[componentKeys[i]] = {
                status: "pending",
                value: null,
            };
        }
    };

    useImperativeHandle(ref,
        () => ({
            start,
            onPrevious,
            inCancelFlow,
            onCancelFlow,
            stack,
        }),
    );

    const generateWorkflow = () => {
        if (!workflow) {
            return null;
        }
        if (_.get(workflow, "inline", false)) {
            return inlineWorkflow();
        } else {
            return modalWorkflow();
        }
    }

    return (
        <>
            { (currentComponentId !== null) && generateWorkflow() }
        </>
    );
});

export default Workflow;
